import { FC, useState } from 'react';
import classNames from 'classnames';
import { useForm, FormProvider, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { isApolloError } from '@apollo/client';
import { Section } from 'jbc-front/components/Form';
import Button from 'jbc-front/components/Button';
import { Modal } from 'jbc-front/components/presenters/ui/Modal';
import { Label } from 'jbc-front/components/presenters/form/Label';
import { Spacer } from 'jbc-front/components/presenters/Spacer';
import { useMutation } from '@/hooks/graphql/useMutation';
import { useNotify } from '@/hooks/useNotify';
import { FileField } from '@/components/react-hook-form/FileField';
import { RegularHealthCheckupReport } from '../../query';
import {
  SEND_REGULAR_HEALTH_CHECKUP_REPORT_TO_EGOV,
  SendRegularHealthCheckupReportToEgovQueryVariables,
  SendRegularHealthCheckupReportToEgovQueryResult,
  EgovValidationErrors,
  EgovFormValidationError
} from './query';
import { Schema, schema } from './schema';
import styles from './EgovModal.scss';

interface EgovModalProps {
  report: RegularHealthCheckupReport;
}

export const EgovModal: FC<EgovModalProps> = ({ report }) => {
  const notify = useNotify();
  const [step, setStep] = useState<'close' | 'form' | 'error'>('close');
  const [validationErrors, setValidationErrors] = useState<EgovValidationErrors | null>(null);
  const methods = useForm<Schema>({
    resolver: yupResolver(schema),
    defaultValues: {
      attachments: []
    }
  });
  const {
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting }
  } = methods;
  const attachments = watch('attachments');

  const [sendRegularHealthCheckupReportToEgov] = useMutation<
    SendRegularHealthCheckupReportToEgovQueryResult,
    SendRegularHealthCheckupReportToEgovQueryVariables
  >(SEND_REGULAR_HEALTH_CHECKUP_REPORT_TO_EGOV);

  const handleCloseModal = () => {
    if (isSubmitting) {
      return;
    }
    setStep('close');
    reset();
  };

  const removeFile = (index: number) => {
    const newAttachments = [...attachments];
    newAttachments.splice(index, 1);
    setValue('attachments', newAttachments);
  };
  const handleDropFile = (files: File[]) => {
    setValue('attachments', [...attachments, ...files]);
  };

  const onSubmit: SubmitHandler<Schema> = async data => {
    try {
      await sendRegularHealthCheckupReportToEgov({
        variables: {
          input: {
            id: report.id,
            attachments: data.attachments
          }
        }
      });

      notify('申請準備が完了しました。ジョブカン労務HRの電子申請一覧より申請してください。', 'success');
      setStep('close');
      reset();
    } catch (err) {
      if (
        err instanceof Error &&
        isApolloError(err) &&
        err.graphQLErrors[0]?.extensions?.code === 'EGOV_INVALID_FORM'
      ) {
        const errors = err.graphQLErrors[0].extensions.egovValidationErrors as EgovValidationErrors;
        setValidationErrors(errors);
        setStep('error');
        return;
      }
      throw err;
    }
  };

  return (
    <FormProvider {...methods}>
      <Button
        primary
        widthAuto
        onClick={() => setStep('form')}
        disabled={!report.egovProcedureSendable}
        disabledReason="既に申請済みです">
        電子申請
      </Button>

      <Modal className={styles.modal} isOpen={step === 'form'} onClose={handleCloseModal}>
        <Modal.Header onClose={handleCloseModal}>電子申請（e-Gov連携）</Modal.Header>
        <Modal.Body>
          <div>
            <Label className={styles.bold}>添付書類</Label>
            <Spacer size={8} />
            <FileField
              name="attachments"
              multiple
              onDrop={handleDropFile}
              error={Array.isArray(errors.attachments) && errors.attachments.find(e => e?.message)?.message}>
              {attachments && (
                <FileField.FileContainer>
                  {attachments.map((attachment, index) => (
                    <FileField.File key={index} file={attachment} onRemove={() => removeFile(index)} />
                  ))}
                </FileField.FileContainer>
              )}
            </FileField>
          </div>
          <div className="u-mt20">
            <p className={classNames(styles.bold, styles.warning)}>【注意】</p>
            <p>申請はジョブカン労務HRに登録されている情報を利用して行います。</p>
            <p>申請前に登録情報が正しいかご確認ください。</p>
            <p className={classNames('u-mt20', styles.warning)}>
              申請はジョブカン労務HRの「電子申請一覧」の画面より行うことができます。
            </p>
            <p>
              労務HRの全権管理者、事業所管理者、運用担当者でない場合は、権限の変更を受けるか、権限を持つ管理者に申請を依頼してください。
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer className={styles.modalFooter}>
          <Button className={styles.footerButton} onClick={handleCloseModal} disabled={isSubmitting}>
            キャンセル
          </Button>
          <Button className={styles.footerButton} primary onClick={handleSubmit(onSubmit)} disabled={isSubmitting}>
            申請を準備する
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal className={styles.modal} isOpen={step === 'error'} onClose={handleCloseModal}>
        <Modal.Header onClose={handleCloseModal}>電子申請（e-Gov連携）</Modal.Header>
        <Modal.Body className={styles.errorModalBody}>
          <Section title="入力内容に誤りがあります。修正後に再度申請をお願いします" error>
            {validationErrors?.precheck && (
              <ul>
                {validationErrors.precheck.map((error, index) => (
                  <li key={index} className={styles.error}>
                    {error}
                  </li>
                ))}
              </ul>
            )}
            {validationErrors?.form?.map((error, index) => <FormValidationError key={index} error={error} />)}
          </Section>
        </Modal.Body>
        <Modal.Footer className={styles.modalFooter}>
          <Button className={styles.footerButton} onClick={handleCloseModal}>
            閉じる
          </Button>
        </Modal.Footer>
      </Modal>
    </FormProvider>
  );
};

interface FormValidationErrorProps {
  error: EgovFormValidationError;
}

const FormValidationError: FC<FormValidationErrorProps> = ({ error: { errorMessage, input = '', ranges = [] } }) => {
  interface InputChunk {
    inputSubStr: string;
    highlight: boolean;
  }

  const chars = [...input];
  const { inputChunk: inputChunkInit, prevEnd } = ranges.reduce<{ inputChunk: InputChunk[]; prevEnd: number }>(
    ({ inputChunk: prevInputChunk, prevEnd }, { begin, end }) => ({
      inputChunk: [
        ...prevInputChunk,
        { inputSubStr: chars.slice(prevEnd + 1, begin).join(''), highlight: false },
        { inputSubStr: chars.slice(begin, end + 1).join(''), highlight: true }
      ],
      prevEnd: end
    }),
    {
      inputChunk: [],
      prevEnd: -1
    }
  );
  const inputChunk = [...inputChunkInit, { inputSubStr: chars.slice(prevEnd + 1).join(''), highlight: false }];
  const highlightedInput = inputChunk.map(({ inputSubStr, highlight }, i) => (
    <span key={i} {...(highlight && { className: styles.invalidRangeText })}>
      {inputSubStr}
    </span>
  ));
  return (
    <div className={styles.formValidationError}>
      <div>{errorMessage}</div>
      {input && <div>入力値：{highlightedInput}</div>}
    </div>
  );
};
